import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';
import katex from 'katex';
import { DataDisplayBlock } from '../../../model';

@Component({
  selector: 'dojo-data-display-block-renderer',
  standalone: true,
  template: ` <div class="data-display-content" [innerHTML]="renderedHtml()"></div> `,
  styles: `
    :host {
      @apply block my-4;
    }

    .data-display-content {
      display: flex;
      flex-wrap: wrap;
      justify-content: center;
      margin: -0.5px;

      ::ng-deep .data-cell {
        display: flex;
        align-items: center;
        justify-content: center;
        padding: 4px 10px;
        border: 0.5px solid var(--neutral-200);
        font-size: 1rem;
        line-height: 1.5;
        white-space: nowrap;
        background: var(--neutral-50);
        color: var(--neutral-800);
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DataDisplayBlockRendererComponent {
  private readonly sanitizer = inject(DomSanitizer);

  readonly block = input.required<DataDisplayBlock>();

  readonly renderedHtml = computed<SafeHtml>(() => {
    const raw = this.block().data;
    if (!raw || !raw.trim()) {
      return this.sanitizer.bypassSecurityTrustHtml('');
    }

    const values = raw
      .split(',')
      .map((v) => v.trim())
      .filter((v) => v.length > 0);

    try {
      const parts = values.map((v) => {
        const rendered = katex.renderToString(v, { throwOnError: false, displayMode: false, output: 'html' });
        return `<span class="data-cell">${rendered}</span>`;
      });
      return this.sanitizer.bypassSecurityTrustHtml(parts.join(''));
    } catch {
      return this.sanitizer.bypassSecurityTrustHtml(raw);
    }
  });
}
